import { Descriptions as AntdDescriptions, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import DriverClassificationContext from "../../../context/driver-classification/context";
import DriverClassificationContextProvider from "../../../context/driver-classification/provider";

interface Props {}

const DriverClassificationDetailsComponent: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const { details, actions, loading } = useContext(
    DriverClassificationContext
  );
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction]);

  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("driver_classification")}
        buttonName={`${t("update_driver_classification")}`}
        onClick={() => navigate(`/driver-classifications/update/${id}`)}
        loading={loading.includes("details")}
        onRefresh={() => actions.getDetails(Number(id))}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <Descriptions size="small" column={1} bordered>
            <AntdDescriptions.Item label={t("id")}>
              {details?.id}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("classification_name")}>
              {details?.name ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("description")}>
              {details?.description ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("calculate_cost_per_km")}>
              {details?.calculatingDeliveryCostPerKilometer ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t("yes")}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="default">
                  {t("no")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("delivery_cost_per_km")}>
              {details?.deliveryCostPerKilometer} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("fixed_order_delivery_cost")}>
              {details?.orderDeliveryCost} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("extra_order_fixed_bonus_enabled")}>
              {details?.extraOrderFixedBonusEnabled ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t("yes")}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="default">
                  {t("no")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("extra_order_fixed_bonus_amount")}>
              {details?.extraOrderFixedBonusAmount} {t("currency")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_at")}>
              {details?.createdAt
                ? new Date(details.createdAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_by")}>
              {details?.createdBy
                ? `${details.createdBy.firstName ?? ""} ${details.createdBy.lastName ?? ""}`.trim() || "-"
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_at")}>
              {details?.updatedAt
                ? new Date(details.updatedAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_by")}>
              {details?.updatedBy
                ? `${details.updatedBy.firstName ?? ""} ${details.updatedBy.lastName ?? ""}`.trim() || "-"
                : "-"}
            </AntdDescriptions.Item>
          </Descriptions>
        )}
      </PageDetails>
    </>
  );
};

const DriverClassificationDetailsPage = () => {
  return (
    <DriverClassificationContextProvider>
      <DriverClassificationDetailsComponent />
    </DriverClassificationContextProvider>
  );
};

export default DriverClassificationDetailsPage;
